import { useMemo, useState, type ReactNode } from "react";
import { motion } from "motion/react";
import { useSectionParallax } from "../../../hooks/useSectionParallax";
import { posthog } from "../../../lib/posthog";
import { BuildAnalysis } from "./playground/playgroundData";
import { CompareChart } from "./playground/CompareChart";
import { ScatterChart } from "./playground/ScatterChart";
import { SurvivalChart } from "./playground/SurvivalChart";
import { Legend, SERIES } from "./playground/ChartKit";
import { BarPreview, LinePreview, PiePreview, HeatmapPreview, HistogramPreview } from "./gallery/BasicCharts";
import { ViolinPreview, QQPreview, RocPreview } from "./gallery/DistributionCharts";

// Glyph ink: muted at rest, iViz blue when hovered or selected (the tile is the `group`).
const INK = "stroke-[#5C5C66] dark:stroke-[#9A9A9A] group-hover:stroke-[#1E4FD8] dark:group-hover:stroke-[#4A90D9] group-aria-pressed:stroke-[#1E4FD8] dark:group-aria-pressed:stroke-[#4A90D9] transition-colors";
const FILL = "fill-[#5C5C66]/40 dark:fill-[#9A9A9A]/40 group-hover:fill-[#1E4FD8] dark:group-hover:fill-[#4A90D9] group-aria-pressed:fill-[#1E4FD8] dark:group-aria-pressed:fill-[#4A90D9] transition-colors";
const LINE = { fill: "none", strokeWidth: 2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, className: INK };

const Bars = (hs: number[], w = 7) => hs.map((h, i) => <rect key={i} x={6 + i * (w + 3)} y={36 - h} width={w} height={h} rx={1.5} className={FILL} />);
const Dots = (pts: [number, number][]) => pts.map(([x, y], i) => <circle key={i} cx={x} cy={y} r={2.2} className={FILL} />);
const GROUP_LEGEND = <div className="mb-2"><Legend items={SERIES.slice(0, 2).map((s) => ({ label: s.name, swatch: s.swatch }))} /></div>;

function ScatterSample() {
  const a = useMemo(() => BuildAnalysis("correlation", 0, 3), []);
  return a.chart === "scatter" ? <ScatterChart points={a.points} xLabel={a.xLabel} yLabel={a.yLabel} /> : null;
}
function LineScatterSample() {
  const a = useMemo(() => BuildAnalysis("regression", 0, 3), []);
  return a.chart === "scatter" ? <>
    <div className="mb-2"><Legend items={[{ label: "Observations", swatch: SERIES[0].swatch }, { label: "Fitted line", swatch: SERIES[1].swatch }]} /></div>
    <ScatterChart points={a.points} curve={a.curve} xLabel={a.xLabel} yLabel={a.yLabel} />
  </> : null;
}
function BoxSample() {
  const a = useMemo(() => BuildAnalysis("two-groups", 0, 3), []);
  return a.chart === "box" ? <>{GROUP_LEGEND}<CompareChart groups={a.groups} /></> : null;
}
function SurvivalSample() {
  const a = useMemo(() => BuildAnalysis("survival", 0, 3), []);
  return a.chart === "survival" ? <>{GROUP_LEGEND}<SurvivalChart curves={a.curves} maxTime={a.maxTime} /></> : null;
}

// Every chart type Flow supports today, each with a live sample rendering.
const CHARTS: { name: string; use: string; glyph: ReactNode; Preview: () => ReactNode }[] = [
  { name: "Bar", use: "Compare a value across categories.", glyph: Bars([14, 24, 18, 30, 22]), Preview: BarPreview },
  { name: "Line", use: "Follow how values change over time.", glyph: <path d="M6 30 L18 20 L28 24 L40 12 L56 16" {...LINE} />, Preview: LinePreview },
  { name: "Scatter", use: "See how two variables move together.", glyph: Dots([[10, 30], [16, 24], [22, 27], [28, 18], [34, 20], [40, 12], [48, 14], [54, 8]]), Preview: ScatterSample },
  { name: "Line + scatter", use: "Overlay a fitted or reference line on observations.", glyph: <><path d="M8 30 L56 10" {...LINE} />{Dots([[12, 26], [22, 25], [30, 18], [40, 17], [50, 10]])}</>, Preview: LineScatterSample },
  { name: "Pie", use: "Show each part's share of a whole.", glyph: <><circle cx={32} cy={21} r={14} {...LINE} /><path d="M32 21 V7 A14 14 0 0 1 45.3 25.3 Z" className={FILL} /></>, Preview: PiePreview },
  { name: "Heatmap", use: "Spot patterns across two dimensions at once.", glyph: <>{[0, 1, 2].flatMap((r) => [0, 1, 2, 3, 4].map((c) => <rect key={`${r}${c}`} x={8 + c * 10} y={6 + r * 10} width={8} height={8} rx={1.5} className={FILL} style={{ opacity: 0.25 + ((r * 5 + c) % 4) * 0.25 }} />))}</>, Preview: HeatmapPreview },
  { name: "Box", use: "Compare distributions: median, spread, and outliers.", glyph: <><path d="M20 8 V14 M20 28 V34 M44 12 V17 M44 30 V36" {...LINE} /><rect x={14} y={14} width={12} height={14} rx={2} {...LINE} /><rect x={38} y={17} width={12} height={13} rx={2} {...LINE} /></>, Preview: BoxSample },
  { name: "Violin", use: "See the full shape of each group's distribution.", glyph: <path d="M22 6 C21 12 14 18 15 25 C16 31 21 32 22 36 C23 32 28 31 29 25 C30 18 23 12 22 6 Z M44 8 C43 16 37 20 38 26 C39 30 43 32 44 36 C45 32 49 30 50 26 C51 20 45 16 44 8 Z" {...LINE} />, Preview: ViolinPreview },
  { name: "Histogram", use: "See how a single variable is distributed.", glyph: Bars([6, 14, 24, 30, 22, 12, 6], 5), Preview: HistogramPreview },
  { name: "Q–Q plot", use: "Check whether data follows a normal distribution.", glyph: <><path d="M8 34 L56 8" {...LINE} strokeDasharray="3 3" />{Dots([[10, 33], [18, 29], [24, 25], [30, 22], [36, 19], [42, 15], [50, 12], [55, 7]])}</>, Preview: QQPreview },
  { name: "ROC curve", use: "Judge how well a classifier separates two outcomes.", glyph: <><path d="M8 34 L56 8" {...LINE} strokeDasharray="3 3" /><path d="M8 34 C10 16 20 10 56 8" {...LINE} /></>, Preview: RocPreview },
  { name: "Kaplan–Meier", use: "Compare time-to-event curves between groups.", glyph: <path d="M6 8 H16 V14 H26 V19 H34 V26 H46 V30 H58" {...LINE} />, Preview: SurvivalSample },
];

function ChartTiles({ active, onSelect }: { active: number; onSelect: (i: number) => void }) {
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
      {CHARTS.map(({ name, glyph }, i) => (
        <button
          key={name} aria-pressed={active === i} onClick={() => onSelect(i)}
          className="group rounded-2xl border p-3 sm:p-4 text-left transition-[transform,border-color,background-color] hover:-translate-y-1 border-[#0A0A0F]/10 dark:border-white/10 bg-white dark:bg-[#0D0D10] hover:border-[#1E4FD8]/40 dark:hover:border-[#4A90D9]/40 aria-pressed:border-[#1E4FD8]/60 dark:aria-pressed:border-[#4A90D9]/60 aria-pressed:bg-[#1E4FD8]/5 dark:aria-pressed:bg-[#4A90D9]/10"
        >
          <svg viewBox="0 0 64 42" className="w-full h-auto" aria-hidden>{glyph}</svg>
          <p className="mt-2 text-[11px] sm:text-xs font-medium text-[#0A0A0F] dark:text-white">{name}</p>
        </button>
      ))}
    </div>
  );
}

function PreviewPanel({ index }: { index: number }) {
  const { name, use, Preview } = CHARTS[index];
  return (
    <div className="mt-5 rounded-3xl border border-[#0A0A0F]/10 dark:border-white/10 bg-white dark:bg-[#0D0D10] p-5 md:p-8">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
        <p className="text-lg font-medium text-[#0A0A0F] dark:text-white">{name}</p>
        <p className="text-sm text-[#5C5C66] dark:text-[#9A9A9A]">{use}</p>
      </div>
      <div className="max-w-3xl mx-auto"><Preview key={name} /></div>
      <p className="mt-3 text-xs text-[#5C5C66] dark:text-[#9A9A9A]">Live sample output. Hover the chart to inspect values.</p>
    </div>
  );
}

export function ChartGallery() {
  const { ref, bgY, fgY } = useSectionParallax();
  const [active, setActive] = useState(0);
  const select = (i: number) => { setActive(i); posthog.capture("iviz_chart_preview", { chart: CHARTS[i].name }); };
  return (
    <div ref={ref}>
      <motion.div style={{ y: fgY }} className="text-center max-w-2xl mx-auto mb-12">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#1E4FD8] dark:text-[#4A90D9] mb-3">Flow · Charts</p>
        <h3 className="text-3xl font-normal tracking-[-0.02em] text-[#0A0A0F] dark:text-white mb-4">See your data every way.</h3>
        <p className="text-base leading-[1.7] text-[#5C5C66] dark:text-[#9A9A9A]">Pick a chart to see what it produces, drawn live from sample data.</p>
      </motion.div>
      <motion.div style={{ y: bgY }} className="max-w-6xl mx-auto">
        <ChartTiles active={active} onSelect={select} />
        <PreviewPanel index={active} />
      </motion.div>
    </div>
  );
}
